import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../env';
import {
  PushDevice,
  TestNotificationKind,
  TestNotificationResult,
  NotificationPreference,
  NotificationPreferenceUpdate
} from '../../models/push-device';

@Injectable({
  providedIn: 'root'
})
export class PushDeviceService {

  constructor(private http: HttpClient) {
  }

  /** Lists the current user's active push devices. */
  listDevices(): Observable<PushDevice[]> {
    return this.http.get<PushDevice[]>(`${environment.apiUrl}/devices`);
  }

  /**
   * Sends a test push notification to all of the current user's devices.
   *
   * `kind` picks which notification to imitate — `consumption_check` carries the
   * actionable category and deep-link payload of a real reminder, so the tap
   * handler can be built before the feature that sends it exists. Nothing is
   * created or changed either way.
   */
  sendTest(kind: TestNotificationKind = 'test'): Observable<TestNotificationResult> {
    return this.http.post<TestNotificationResult>(`${environment.apiUrl}/devices/test`, {type: kind});
  }

  /**
   * Registers a synthetic device (server-generated token) so push delivery can
   * be tested without a physical device. Non-production environments only; the
   * backend returns 404 elsewhere.
   */
  addFake(env: 'sandbox' | 'production' = 'sandbox'): Observable<PushDevice> {
    return this.http.post<PushDevice>(`${environment.apiUrl}/devices/fake`, {environment: env});
  }

  /** Removes one of the current user's devices by id (the client never holds the full token). */
  remove(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/devices/by-id/${id}`);
  }

  /** Lists the notification-type catalog merged with the user's on/off state. */
  getPreferences(): Observable<NotificationPreference[]> {
    return this.http.get<NotificationPreference[]>(`${environment.apiUrl}/notification-preferences`);
  }

  /** Bulk-updates notification-type toggles; returns the fresh merged state. */
  updatePreferences(preferences: NotificationPreferenceUpdate[]): Observable<NotificationPreference[]> {
    return this.http.put<NotificationPreference[]>(
      `${environment.apiUrl}/notification-preferences`,
      {preferences}
    );
  }
}
